/**
 * Ciclo de sincronização (plano, 19.2 item 4): ao abrir o app, ao voltar a rede,
 * a cada 60 segundos com o app aberto e, no Android, por Background Sync.
 */
import { useRuntime, useSession, useSyncStatus } from '@lindeza/core/hooks';
import { useEffect } from 'react';

export function useSyncLoop(): void {
  const rt = useRuntime();
  const mode = useSession((s) => s.mode);
  const token = useSession((s) => s.token);
  const status = useSyncStatus();

  useEffect(() => {
    if (mode !== 'account' || !token) return;
    const run = () => void rt.sync.sync();
    run();
    const interval = window.setInterval(() => {
      if (document.visibilityState === 'visible') run();
    }, 60_000);
    const onOnline = () => run();
    const onVisible = () => {
      if (document.visibilityState === 'visible') run();
    };
    const onMessage = (e: MessageEvent<{ type?: string }>) => {
      if (e.data?.type === 'lindeza-sync') run();
    };
    window.addEventListener('online', onOnline);
    document.addEventListener('visibilitychange', onVisible);
    navigator.serviceWorker?.addEventListener('message', onMessage);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener('online', onOnline);
      document.removeEventListener('visibilitychange', onVisible);
      navigator.serviceWorker?.removeEventListener('message', onMessage);
    };
  }, [rt, mode, token]);

  // Alteração local nova: tenta enviar logo (com uma pequena espera para juntar várias).
  useEffect(() => {
    if (mode !== 'account' || !token || status.pending === 0) return;
    if (status.state === 'syncing') return;
    const t = window.setTimeout(() => {
      if (navigator.onLine) void rt.sync.sync();
      else void registerBackgroundSync();
    }, 1500);
    return () => window.clearTimeout(t);
  }, [rt, mode, token, status.pending, status.state]);
}

/** Background Sync (Android/Chrome): o service worker envia a fila quando a rede voltar. */
async function registerBackgroundSync(): Promise<void> {
  try {
    const reg = (await navigator.serviceWorker?.ready) as
      | (ServiceWorkerRegistration & { sync?: { register(tag: string): Promise<void> } })
      | undefined;
    await reg?.sync?.register('lindeza-sync');
  } catch {
    // iPhone e navegadores sem suporte: sincroniza ao abrir o app.
  }
}
